import React, { useEffect, useState, useCallback } from 'react';
import api from '../../api';
import { Loader, EmptyState, ConfirmDialog, IconPicker } from '../../components/Shared';
import { useToast } from '../../context/AppContext';

function ServiceForm({ service, onSave, onClose }) {
  const { addToast } = useToast();
  const [form, setForm] = useState({
    name:        service?.name        || '',
    description: service?.description || '',
    icon:        service?.icon        || 'bi-gear',
    icon_color:  service?.icon_color  || '#B8860B',
    is_featured: service?.is_featured ?? 1,
    sort_order:  service?.sort_order  ?? 0,
    status:      service?.status      || 'active',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      if (service) { await api.put(`/services/${service.id}`, form); addToast('Service updated!', 'success'); }
      else { await api.post('/services', form); addToast('Service created!', 'success'); }
      onSave();
    } catch (err) {
      addToast(err.response?.data?.message || 'Failed to save', 'error');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" style={{ maxWidth: 580 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h5 style={{ color: 'var(--text)', margin: 0, fontFamily: 'var(--font-display)' }}>
            {service ? 'Edit Service' : 'New Service'}
          </h5>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 22, cursor: 'pointer' }}>
            <i className="bi bi-x" />
          </button>
        </div>
        <form onSubmit={handleSave}>
          <div className="modal-body">
            <div className="row g-3">
              <div className="col-12">
                <label className="form-label-styled">Service Name *</label>
                <input className="form-control-styled" required placeholder="e.g. General Construction"
                  value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div className="col-12">
                <label className="form-label-styled">Description *</label>
                <textarea className="form-control-styled" rows={4} required placeholder="Describe this service..."
                  value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
              </div>
              <div className="col-12">
                <label className="form-label-styled">Icon</label>
                <IconPicker value={form.icon} onChange={icon => setForm(f => ({ ...f, icon }))} />
              </div>
              <div className="col-sm-6">
                <label className="form-label-styled">Icon Color</label>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <input type="color" value={form.icon_color}
                    onChange={e => setForm(f => ({ ...f, icon_color: e.target.value }))}
                    style={{ width: 44, height: 40, border: '1.5px solid var(--border)', borderRadius: 'var(--radius)', cursor: 'pointer', padding: 2 }} />
                  <input className="form-control-styled" value={form.icon_color}
                    onChange={e => setForm(f => ({ ...f, icon_color: e.target.value }))} style={{ flex: 1 }} />
                </div>
              </div>
              <div className="col-sm-3">
                <label className="form-label-styled">Sort Order</label>
                <input type="number" className="form-control-styled" value={form.sort_order}
                  onChange={e => setForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))} />
              </div>
              <div className="col-sm-3">
                <label className="form-label-styled">Featured</label>
                <select className="form-control-styled" value={form.is_featured}
                  onChange={e => setForm(f => ({ ...f, is_featured: parseInt(e.target.value) }))}>
                  <option value={1}>Yes</option>
                  <option value={0}>No</option>
                </select>
              </div>
              <div className="col-sm-6">
                <label className="form-label-styled">Status</label>
                <select className="form-control-styled" value={form.status}
                  onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              {/* Live Preview */}
              <div className="col-12">
                <label className="form-label-styled">Preview</label>
                <div style={{ background: 'var(--bg-3)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '20px 18px', display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                  <div style={{ width: 52, height: 52, background: form.icon_color + '18', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <i className={`bi ${form.icon}`} style={{ color: form.icon_color, fontSize: 22 }} />
                  </div>
                  <div>
                    <div style={{ color: 'var(--text)', fontFamily: 'var(--font-display)', fontWeight: 600, marginBottom: 4 }}>{form.name || 'Service Name'}</div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', lineHeight: 1.6 }}>{form.description || 'Description will appear here.'}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn-outline-secondary-styled">Cancel</button>
            <button type="submit" className="btn-primary-styled" disabled={saving}>
              {saving ? 'Saving...' : <><i className="bi bi-check2 me-1" />{service ? 'Update' : 'Create'} Service</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminServicesPage() {
  const [services, setServices]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [editService, setEditService] = useState(null);
  const [showForm, setShowForm]     = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const { addToast } = useToast();

  const load = useCallback(() => {
    setLoading(true);
    api.get('/services').then(r => setServices(r.data.data || [])).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    try {
      await api.delete(`/services/${confirmDelete.id}`);
      addToast('Service deleted', 'success');
      setConfirmDelete(null);
      load();
    } catch (err) {
      addToast(err.response?.data?.message || 'Cannot delete — service is linked to projects.', 'error');
    }
  };

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-3">
        <div>
          <h2 style={{ fontFamily: 'var(--font-display)', color: 'var(--text)', marginBottom: 4 }}>Services</h2>
          <p style={{ color: 'var(--text-muted)', margin: 0, fontSize: '0.875rem' }}>Manage the services displayed on your website</p>
        </div>
        <button onClick={() => { setEditService(null); setShowForm(true); }} className="btn-primary-styled">
          <i className="bi bi-plus-lg" /> New Service
        </button>
      </div>

      {loading ? <Loader /> : services.length === 0 ? (
        <EmptyState icon="bi-tools" title="No services yet" subtitle="Add your first service!" />
      ) : (
        <div className="row g-3">
          {services.map(s => (
            <div key={s.id} className="col-md-6 col-lg-4">
              <div className="admin-card p-4 h-100" style={{ position: 'relative' }}>
                {/* Action buttons */}
                <div style={{ position: 'absolute', top: 14, right: 14, display: 'flex', gap: 6 }}>
                  <button onClick={() => { setEditService(s); setShowForm(true); }}
                    style={{ background: '#EBF4FD', border: '1px solid #BDE0F8', color: '#2980B9', padding: '5px 9px', borderRadius: 'var(--radius)', cursor: 'pointer', fontSize: 12 }}>
                    <i className="bi bi-pencil" />
                  </button>
                  <button onClick={() => setConfirmDelete(s)}
                    style={{ background: '#FDECEA', border: '1px solid #F5C6CB', color: 'var(--danger)', padding: '5px 9px', borderRadius: 'var(--radius)', cursor: 'pointer', fontSize: 12 }}>
                    <i className="bi bi-trash" />
                  </button>
                </div>

                <div style={{ width: 50, height: 50, background: (s.icon_color || '#B8860B') + '18', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                  <i className={`bi ${s.icon}`} style={{ color: s.icon_color || '#B8860B', fontSize: 20 }} />
                </div>
                <h6 style={{ color: 'var(--text)', fontFamily: 'var(--font-display)', marginBottom: 8 }}>{s.name}</h6>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', lineHeight: 1.7, marginBottom: 14 }}>{s.description}</p>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <span className={s.status === 'active' ? 'badge-success-light' : 'badge-secondary-light'}>{s.status}</span>
                  {s.is_featured === 1 && <span className="badge-gold-light"><i className="bi bi-star-fill me-1" />Featured</span>}
                  <span className="badge-secondary-light">Order: {s.sort_order}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <ServiceForm
          service={editService}
          onClose={() => setShowForm(false)}
          onSave={() => { setShowForm(false); load(); }}
        />
      )}
      {confirmDelete && (
        <ConfirmDialog
          message={`Delete "${confirmDelete.name}"? This cannot be undone.`}
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}
